import React, { useEffect, useMemo, useState } from "react";

import { Typography } from "@packages/ds";
import moment from "moment";
import { useIntl } from "react-intl";
import { useParams, useLocation } from "react-router-dom";

import ContentLayout from "Components/ContentLayout";
import ErrorBoundary from "Components/ErrorBoundary";
import config from "console_config";
import Button, { BackLink, Link } from "ds/Button";
import * as Sticker from "ds/Sticker";
import useUrls from "Hooks/useUrls";
import { getTrial, interpolateURL } from "Libs/utils";
import {
  meSelector,
  selectCurrentUserIsInternal
} from "Reducers/app/selectors";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import { useOrganizationProfile } from "Reducers/organization/hooks/useOrganizationProfile";
import {
  deleteSubscription,
  loadingSelector,
  loadSubscription,
  clearOrgSubscriptionError,
  selectSubscriptionDetails,
  errorsSelector,
  orgSubscriptionEstimateSelector
} from "Reducers/organization/subscription";
import { loadOneProject } from "Reducers/project/project";
import { getCommonError } from "Reducers/sliceFactory";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import DeleteSubscriptionModal from "./components/deleteModal/DeleteSubscriptionModal";
import ProjectPlanForm from "./containers/ProjectPlanForm";
import * as S from "./styles";

const PlanDetail = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const { organizationId, subscriptionId } = useParams();
  const [hasPaymentSource, setPaymentSource] = useState();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const user = useAppSelector(meSelector);
  const isInternalUser = useAppSelector(selectCurrentUserIsInternal);

  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );
  const orgId = organization?.id;

  const subscription = useAppSelector(state => {
    return selectSubscriptionDetails(state, {
      organizationId,
      subscriptionId
    });
  });

  const subEstimate = useAppSelector(state =>
    orgSubscriptionEstimateSelector(state, {
      organizationId
    })
  );

  const error = getCommonError(useAppSelector(errorsSelector)).error.message;

  const projectId = subscription?.project_id;

  useEffect(() => {
    projectId &&
      dispatch(
        loadOneProject({
          projectId,
          organizationId,
          loadActivitiesAndSubscribe: false
        })
      );
  }, [projectId, organizationId]);

  const { organizationProfile } = useOrganizationProfile(organizationId);

  const { currency = "", current_trial } = organizationProfile || {};

  useEffect(() => {
    let isCanceled = false;
    const getPaymentSource = async () => {
      if (hasPaymentSource !== undefined || !orgId) return;
      const platformLib = await import("Libs/platform");
      const client = platformLib.default;

      const ps = await client.getOrganizationPaymentSource(orgId);
      if (isCanceled) {
        return;
      }
      setPaymentSource(!!ps?.type);
    };
    if (!isInternalUser) getPaymentSource();

    return () => (isCanceled = true);
  }, [orgId]);

  useEffect(() => {
    if (subscriptionId && organization)
      dispatch(
        loadSubscription({
          organizationId,
          id: subscriptionId
        })
      );
  }, [subscriptionId, organization]);

  useEffect(() => {
    dispatch(clearOrgSubscriptionError());
  }, [dispatch]);

  const showBanner = (() => {
    if (!getTrial(organizationProfile) && !hasPaymentSource) return true;

    if (
      current_trial?.active &&
      moment(current_trial?.expiration).isSameOrBefore(moment())
    )
      return true;

    return false;
  })();

  const isOnTrial = useMemo(() => {
    return getTrial(organizationProfile) && !hasPaymentSource;
  }, [organizationProfile]);

  const canEdit = subscription?.hasLink && subscription?.hasLink("update");
  const isLoadingSubscription = useAppSelector(loadingSelector, () => false);

  const [projectSettingsUrl, projectOverviewUrl] = useUrls(
    ["organization.project.settings", "organization.project"],
    {
      organizationId:
        organization?.name || subscription?.owner || organizationId || "",
      projectId: subscription?.project_id || subscriptionId || ""
    }
  );

  return (
    <ContentLayout className="settings-content">
      <ErrorBoundary>
        <S.HeaderWrapper>
          <BackLink
            to={`/${organizationId}/-/billing/plan`}
            from={location.state?.from || "plans"}
            capitalized={false}
          />
          <S.HeaderContent hasBackButton>
            <S.Container>
              <Typography.Title tag="h2">
                {subscription?.project_title}
              </Typography.Title>
            </S.Container>

            <S.NavigationContainer>
              <Button
                variant="outline"
                to={projectOverviewUrl}
                analyticId="project.morelinks.view"
              >
                {intl.formatMessage({ id: "project.morelinks.view" })}
              </Button>
              <Button
                variant="outline"
                to={projectSettingsUrl}
                analyticId="project_setting"
              >
                {intl.formatMessage({ id: "project_setting" })}
              </Button>
            </S.NavigationContainer>
          </S.HeaderContent>
        </S.HeaderWrapper>
        <S.StickerLayout>
          {showBanner && (
            <Sticker.Root priority="normal" className="sticker-message">
              <Sticker.Icon priority="normal" />
              <Sticker.Text>
                {intl.formatMessage({
                  id: "settings.plan.banner.trial",
                  defaultMessage:
                    "To upgrade your plan you need to add a payment method to your billing details."
                })}
              </Sticker.Text>
              <Link
                variant="primary"
                to={`/${organizationId}/-/billing`}
                analyticId="settings.plan.banner.button"
              >
                {intl.formatMessage({
                  id: "settings.plan.banner.button",
                  defaultMessage: "Add billing details"
                })}
              </Link>
            </Sticker.Root>
          )}
          {error && (
            <Sticker.Root className="sticker-message" priority="critical">
              <Sticker.Icon priority="critical" />
              <Sticker.Text>{error}</Sticker.Text>
            </Sticker.Root>
          )}
        </S.StickerLayout>
        <S.ProjectPlanFormContainer>
          {subscription && (
            <ProjectPlanForm
              subscription={subscription}
              organizationNameId={organizationId ?? ""}
              organizationProfile={organizationProfile}
              isLoadingSubscription={
                isLoadingSubscription == "idle" ? false : isLoadingSubscription
              }
              error={error}
              isOnTrial={isOnTrial}
              currency={currency}
            />
          )}
        </S.ProjectPlanFormContainer>
        {canEdit && !isLoadingSubscription && (
          <S.DeleteButton
            analyticId="settings.plan.project.delete.button"
            onClick={() => {
              if (config.CUSTOM_URL_SUBSCRIPTION_DELETE_REDIRECT) {
                window.location.href = interpolateURL(
                  config.CUSTOM_URL_SUBSCRIPTION_DELETE_REDIRECT,
                  { projectId }
                );
              } else {
                setOpenDeleteModal(true);
              }
            }}
            variant="critical"
          >
            {intl.formatMessage({
              id: "settings.plan.project.delete.button",
              defaultMessage: "Delete project"
            })}
          </S.DeleteButton>
        )}

        {projectId && (
          <DeleteSubscriptionModal
            isOpen={openDeleteModal}
            onClose={() => setOpenDeleteModal(false)}
            subscription={subscription}
            onDelete={() =>
              dispatch(
                deleteSubscription({
                  subscription,
                  organizationId,
                  // A little redundant as we have a layer that redirect on wrong URL to plans page
                  // but good for future proof
                  settings: { redirect: `/${organizationId}/-/billing/plan` }
                })
              )
            }
          />
        )}
      </ErrorBoundary>
    </ContentLayout>
  );
};

export default PlanDetail;
